import { useCallback, useMemo, useState } from 'react';
import { setSongNoteStatus } from '../../../../services/SongNotesService';
import { noteTeacherLabel, noteDateLabel } from '../notes/CarryForward';

/**
 * The Deliver dialog's state and its write, for BOTH openers (pass 3 C4,
 * 2026-09-28): a note row on the current song, and a Carry-forward item,
 * which may be any song, any teacher, any earlier lesson.
 *
 * Returns { deliverNote, deliverCtx, carryError, openDeliver, closeDeliver,
 * handleDeliver, clearCarryError }. `deliverCtx` is what the dialog shows
 * beside the note (song, section, author) and the choice the opener asked
 * for (`preselect`, null from a note row).
 *
 * TWO WRITE PATHS, ONE RULE. A note on the board (the board hydrates every
 * repertoire song with its notes, so this is nearly every Carry-forward note)
 * goes through `notes.setNoteStatus`, exactly as a note row does: optimistic,
 * legality-checked, and it re-reads Carry-forward on persist, so the item
 * leaves Carry-forward and appears under "recently landed" on its tab. A note
 * NOT on the board (its tab was removed this lesson, or a legacy note whose
 * song no board row matches) has no board state to patch, so it is written
 * directly through the same service call and Carry-forward re-reads. That
 * path's failure is surfaced (`carryError`, shown in the Arrive group with
 * the owner's ask) rather than swallowed. The legality rule (nothing back to
 * captured) is enforced by the service and the rules either way.
 */
const useDeliverNote = ({ board, notes, lessonId = null, uid = null, onPersisted = null }) => {
	const [deliverNote, setDeliverNote] = useState(null);
	const [deliverCtx, setDeliverCtx] = useState(null);
	const [carryError, setCarryError] = useState('');

	const boardSongs = board?.songs;
	const songs = useMemo(() => boardSongs || [], [boardSongs]);
	const songOf = useCallback((noteId) => songs.find((s) => (s.notes || []).some((n) => n.id === noteId)) || null, [songs]);

	const openDeliver = useCallback(
		(note, { preselect = null, songName = null } = {}) => {
			if (!note) return;
			const song = songOf(note.id);
			setDeliverCtx({
				preselect,
				songName: songName || song?.name || null,
				sectionLabel: note.sectionLabel || null,
				authorLabel: [noteTeacherLabel(note, uid), noteDateLabel(note)].filter(Boolean).join(', ') || null,
			});
			setDeliverNote(note);
		},
		[songOf, uid]
	);

	const closeDeliver = useCallback(() => {
		setDeliverNote(null);
		setDeliverCtx(null);
	}, []);

	const handleDeliver = useCallback(
		async (noteId, toStatus) => {
			if (songOf(noteId)) return notes.setNoteStatus(noteId, toStatus);
			const note = deliverNote && deliverNote.id === noteId ? deliverNote : null;
			if (!note || note.status === toStatus) return false;
			setCarryError('');
			try {
				await setSongNoteStatus(noteId, { from: note.status, to: toStatus, lessonId, byTeacher: uid });
				if (onPersisted) onPersisted();
				return true;
			} catch (error) {
				console.error('Error delivering a Carry-forward note:', error);
				setCarryError((error && error.message) || 'unknown error');
				return false;
			}
		},
		[songOf, notes, deliverNote, lessonId, uid, onPersisted]
	);

	const clearCarryError = useCallback(() => setCarryError(''), []);

	return { deliverNote, deliverCtx, carryError, openDeliver, closeDeliver, handleDeliver, clearCarryError };
};

export default useDeliverNote;
